import { useState, useCallback } from 'react';

/**
 * Configuração do hook genérico de drill down
 * Princípio DIP: Dependência de abstrações, não de concretizações
 */
interface GenericDrillDownConfig<TData = any, TItem = any> {
  id?: string;
  title?: string;
  endpoint: string;
  buildUrl: (baseUrl: string, params: TItem) => string;
  includeAuth?: boolean;
  errorHandler?: (error: Error) => string;
  dataTransformer?: (data: any) => TData;
  breadcrumbConfig?: {
    label: string;
    getBreadcrumbItems: (data: TData) => Array<{ label: string; value?: string }>;
  };
  chartConfig?: {
    type: 'bar' | 'line' | 'pie' | 'area';
    dataKey: string;
    valueKey: string;
    color?: string | string[];
    height?: number;
  };
  tableConfig?: {
    columns: Array<{
      key: string;
      label: string;
      align?: 'left' | 'center' | 'right';
      format?: (value: any) => string;
      width?: string | number;
    }>;
    pagination?: boolean;
    rowsPerPage?: number;
  };
}

/**
 * Interface de retorno do hook
 * Princípio ISP: Interface segregada com responsabilidades específicas
 */
interface UseGenericDrillDownReturn<TData = any, TItem = any> {
  isModalOpen: boolean;
  data: TData | null;
  loading: boolean;
  error: string | null;
  selectedItem: TItem | null;
  openModal: (item: TItem) => Promise<void>;
  closeModal: () => void;
  retry: () => Promise<void>;
}

/**
 * Hook genérico para drill downs
 * Princípio SRP: Responsável apenas por gerenciar estado e requisições de drill down
 * Princípio OCP: Extensível via configuração sem modificar código existente
 * Princípio DIP: Aceita configuração como dependência
 */
export const useGenericDrillDown = <TData = any, TItem = any>(
  config: GenericDrillDownConfig<TData, TItem>
): UseGenericDrillDownReturn<TData, TItem> => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<TItem | null>(null);

  const {
    endpoint,
    buildUrl,
    includeAuth = true,
    errorHandler = (error: Error) => error.message,
    dataTransformer = (data: any) => data
  } = config;

  /**
   * Constrói headers da requisição
   * Princípio SRP: Responsável apenas pela construção de headers
   */
  const buildHeaders = useCallback((): HeadersInit => {
    const baseHeaders: HeadersInit = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };

    if (includeAuth) {
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("authTokenMHP="))
        ?.split("=")[1];

      if (token) {
        baseHeaders['Authorization'] = `Bearer ${token}`;
      }
    }

    return baseHeaders;
  }, [includeAuth]);

  /**
   * Faz requisição para API
   * Princípio SRP: Responsável apenas pela requisição HTTP
   */
  const fetchData = useCallback(async (item: TItem): Promise<TData | null> => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const url = buildUrl(baseUrl, item);
    const headers = buildHeaders();

    const response = await fetch(url, { headers });

        if (!response.ok) {
      // Tentar extrair mensagem de erro do body
      try {
        const errorData = await response.json();
        if (errorData && (errorData.erro || errorData.message)) {
          throw new Error(errorData.erro || errorData.message);
        }
      } catch (jsonError) {
        // Se não conseguir fazer parse do JSON ou não houver erro específico, usar erro HTTP padrão
        // Mas primeiro verificar se já criamos um erro customizado
        if (jsonError instanceof Error && (jsonError.message !== `HTTP error! status: ${response.status}`)) {
          throw jsonError;
        }
      }
      // Fallback para erro HTTP padrão
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const rawData = await response.json();
    return dataTransformer(rawData);
  }, [buildUrl, buildHeaders, dataTransformer]);

  /**
   * Abre modal e carrega dados
   * Princípio SRP: Responsável apenas pela abertura do modal e carregamento
   */
  const openModal = useCallback(async (item: TItem): Promise<void> => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setLoading(true);
    setError(null);

    try {
      const result = await fetchData(item);
      setData(result);
    } catch (err) {
      const errorMessage = errorHandler(err instanceof Error ? err : new Error('Unknown error'));
      setError(errorMessage);
      setData(null);
      console.error('Erro ao buscar dados do drill down:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchData, errorHandler]);

  /**
   * Fecha modal e limpa estado
   * Princípio SRP: Responsável apenas pelo fechamento e limpeza
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setData(null);
    setError(null);
  }, []);

  /**
   * Retenta requisição com item atual
   * Princípio SRP: Responsável apenas pela repetição da requisição
   */
  const retry = useCallback(async (): Promise<void> => {
    if (!selectedItem) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchData(selectedItem);
      setData(result);
    } catch (err) {
      const errorMessage = errorHandler(err instanceof Error ? err : new Error('Unknown error'));
      setError(errorMessage);
      setData(null);
      console.error('Erro ao retentar busca de dados:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedItem, fetchData, errorHandler]);

  return {
    isModalOpen,
    data,
    loading,
    error,
    selectedItem,
    openModal,
    closeModal,
    retry
  };
};

/**
 * Configurações pré-definidas para tipos comuns de drill down
 * Princípio OCP: Extensível com novas configurações sem modificar código existente
 */
export const drillDownConfigs = {
  /**
   * Configuração para drill down de seção de vendas
   */
  secaoVendas: {
    endpoint: 'sped-fiscal/secao-vendas-drill-down',
    buildUrl: (baseUrl: string, params: { spedFiscalId: number; nome: string }) => 
      `${baseUrl}sped-fiscal/secao-vendas-drill-down/${params.spedFiscalId}/${encodeURIComponent(params.nome)}`,
    includeAuth: true
  },

  /**
   * Configuração para drill down de fornecedor
   */
  fornecedor: {
    endpoint: 'sped-fiscal/fornecedor-detalhes-drill-down',
    buildUrl: (baseUrl: string, params: { spedFiscalId: number; nome: string }) => 
      `${baseUrl}sped-fiscal/fornecedor-detalhes-drill-down/${params.spedFiscalId}/${encodeURIComponent(params.nome)}`,
    includeAuth: true
  },

  /**
   * Configuração para drill down de produto
   */
  produto: {
    endpoint: 'produto-detalhes-drill-down',
    buildUrl: (baseUrl: string, params: { spedFiscalId: number; codItem: string; indOper?: string }) => {
      const url = new URL(`${baseUrl}produto-detalhes-drill-down/${params.spedFiscalId}/${params.codItem}`);
      if (params.indOper) {
        url.searchParams.set('ind_oper', params.indOper);
      }
      return url.toString();
    },
    includeAuth: true
  },

  /**
   * Configuração para drill down de evolução de compras
   */
  evolucaoCompras: {
    endpoint: 'sped-fiscal/evolucao-compras-drill-down',
    buildUrl: (baseUrl: string, params: { periodo: string }) => 
      `${baseUrl}sped-fiscal/evolucao-compras-drill-down?periodo=${encodeURIComponent(params.periodo)}`,
    includeAuth: true
  },

  /**
   * Configuração para drill down de evolução de vendas
   */
  evolucaoVendas: {
    endpoint: 'vendas-periodo-drill-down',
    buildUrl: (baseUrl: string, params: { periodo: string }) => 
      `${baseUrl}sped-fiscal/vendas-periodo-drill-down?periodo=${encodeURIComponent(params.periodo)}`,
    includeAuth: true
  }
};

/**
 * Hooks específicos para manter compatibilidade com código existente
 * Princípio OCP: Extensível via composição
 */
export const useSecaoVendasDrillDown = () => {
  return useGenericDrillDown(drillDownConfigs.secaoVendas);
};

export const useFornecedorDrillDown = ({ spedFiscalId }: { spedFiscalId: number | null | undefined }) => {
  const baseConfig = drillDownConfigs.fornecedor;
  
  return useGenericDrillDown({
    ...baseConfig,
    buildUrl: (baseUrl: string, params: { nome: string }) => 
      baseConfig.buildUrl(baseUrl, { spedFiscalId: spedFiscalId || 0, nome: params.nome })
  });
};

export const useProductDrillDown = () => {
  return useGenericDrillDown(drillDownConfigs.produto);
};

export const useEvolucaoComprasDrillDown = (establishmentId?: number | string | null) => {
  const config = {
    id: 'evolucao-compras-periodo',
    title: 'Evolução de Compras por Período',
    endpoint: 'sped-fiscal/compras-periodo-drill-down',
    buildUrl: (baseUrl: string, params: { periodo: string }) => {
      const searchParams = new URLSearchParams();
      if (params.periodo) {
        searchParams.append('periodo', params.periodo);
      }
      if (establishmentId !== undefined && establishmentId !== null && String(establishmentId).trim() !== '') {
        searchParams.append('establishment_id', String(establishmentId));
      }
      const query = searchParams.toString();
      const separator = query ? `?${query}` : '';
      return `${baseUrl}sped-fiscal/compras-periodo-drill-down${separator}`;
    },
    includeAuth: true,
    breadcrumbConfig: {
      label: 'Período',
      getBreadcrumbItems: (data: any) => [
        { label: data.periodo || 'Período' }
      ]
    }
  };

  return useGenericDrillDown<any, { periodo: string }>(config);
};

export const useEvolucaoVendasDrillDown = (establishmentId?: number | string | null) => {
  const config = {
    id: 'evolucao-vendas-periodo',
    title: 'Evolução de Vendas por Período',
    endpoint: 'sped-fiscal/vendas-periodo-drill-down',
    buildUrl: (baseUrl: string, params: { periodo: string }) => {
      const searchParams = new URLSearchParams();
      if (params.periodo) {
        searchParams.append('periodo', params.periodo);
      }
      if (establishmentId !== undefined && establishmentId !== null && String(establishmentId).trim() !== '') {
        searchParams.append('establishment_id', String(establishmentId));
      }
      const query = searchParams.toString();
      const separator = query ? `?${query}` : '';
      return `${baseUrl}sped-fiscal/vendas-periodo-drill-down${separator}`;
    },
    includeAuth: true,
    breadcrumbConfig: {
      label: 'Período',
      getBreadcrumbItems: (data: any) => [
        { label: data.periodo || 'Período' }
      ]
    }
  };

  return useGenericDrillDown<any, { periodo: string }>(config);
}; 